import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
} from '@mui/material';
import StandardizedChip from '../../common/StandardizedChip';
import PaginationBar from '../../common/PaginationBar';
import {
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Security as AnonymizeIcon,
  Visibility as ViewIcon,
  FilterAlt as FilterAltIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { getReferenceTypeColor, formatTimestamp } from './utils';

/**
 * AuditTable Component
 *
 * Displays the audit logs table with sorting and pagination.
 *
 * @param {Object} props
 * @param {Array} props.auditLogs - Array of audit log entries
 * @param {boolean} props.loading - Whether data is loading
 * @param {string} props.sortBy - Current sort column
 * @param {string} props.sortOrder - Current sort order (asc/desc)
 * @param {Function} props.onSort - Callback when sort column is clicked
 * @param {string} props.userFilter - Current user filter value
 * @param {Array} props.uniqueUsers - Array of unique user emails (stored in user_id field)
 * @param {Object} props.userFilterAnchor - Anchor element for user filter menu
 * @param {Function} props.onUserFilterOpen - Callback to open user filter menu
 * @param {Function} props.onUserFilterClose - Callback to close user filter menu
 * @param {Function} props.onUserFilterSelect - Callback when user filter is selected
 * @param {Function} props.onViewDetails - Callback when view details is clicked
 * @param {number} props.page - Current page number (1-based)
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.totalCount - Total count of audit logs
 * @param {number} props.pageSize - Number of items per page
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {Function} props.onPageSizeChange - Callback when page size changes
 */
function AuditTable({
  auditLogs,
  loading,
  sortBy,
  sortOrder,
  onSort,
  userFilter,
  uniqueUsers,
  userFilterAnchor,
  onUserFilterOpen,
  onUserFilterClose,
  onUserFilterSelect,
  onViewDetails,
  page,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) {
  // PaginationBar handlers (1-based pagination)
  const handlePrevPage = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  };

  const handlePageClick = newPage => {
    onPageChange(newPage);
  };

  const handlePageSizeChange = newSize => {
    onPageSizeChange(newSize);
  };
  const isUserFilterActive = userFilter !== '';

  const renderSortIcon = column => {
    if (sortBy !== column) {
      return null;
    }
    return sortOrder === 'asc' ? (
      <ArrowUpwardIcon
        fontSize="small"
        sx={{ ml: 0.5, verticalAlign: 'middle' }}
      />
    ) : (
      <ArrowDownwardIcon
        fontSize="small"
        sx={{ ml: 0.5, verticalAlign: 'middle' }}
      />
    );
  };

  return (
    <Card>
      {loading && <LinearProgress />}
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Content Check Audit Logs
        </Typography>

        <TableContainer component={Paper}>
          <Table sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSort('timestamp')}
                >
                  Timestamp {renderSortIcon('timestamp')}
                </TableCell>
                <TableCell>Reference</TableCell>
                <TableCell
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    position: 'relative',
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center">
                      User
                    </Box>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        onUserFilterOpen(e);
                      }}
                      sx={{
                        ml: 1,
                        p: 0.5,
                        color: isUserFilterActive ? 'primary.main' : 'inherit',
                      }}
                    >
                      <FilterAltIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Menu
                    anchorEl={userFilterAnchor}
                    open={Boolean(userFilterAnchor)}
                    onClose={onUserFilterClose}
                    onClick={e => e.stopPropagation()}
                  >
                    <MenuItem
                      onClick={() => onUserFilterSelect('all')}
                      selected={userFilter === ''}
                    >
                      All Users
                    </MenuItem>
                    {uniqueUsers.map(userId => (
                      <MenuItem
                        key={userId}
                        onClick={() => onUserFilterSelect(userId)}
                        selected={userFilter === userId}
                      >
                        {userId}
                      </MenuItem>
                    ))}
                  </Menu>
                </TableCell>
                <TableCell>Content Preview</TableCell>
                <TableCell>Violations</TableCell>
                <TableCell>Risk Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLogs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <ScheduleIcon sx={{ mr: 1, fontSize: 16 }} />
                      {formatTimestamp(log.timestamp)}
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 200,
                      maxWidth: 400,
                      wordBreak: 'break-all',
                      overflowWrap: 'break-word',
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          width: 150,
                          height: 24,
                          mb: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Tooltip
                          title={log.reference_type || 'Unknown'}
                          arrow
                          enterDelay={300}
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '1.25rem',
                              },
                            },
                          }}
                        >
                          <Chip
                            label={log.reference_type || 'Unknown'}
                            color={getReferenceTypeColor(log.reference_type)}
                            size="small"
                            sx={{
                              width: '100%',
                              maxWidth: '150px',
                              height: '24px',
                              padding: 0,
                              boxSizing: 'border-box',
                              '& .MuiChip-label': {
                                px: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Tooltip
                        title={log.reference_id || 'N/A'}
                        arrow
                        enterDelay={300}
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '1.25rem',
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {log.reference_id || 'N/A'}
                        </Typography>
                      </Tooltip>
                      {log.template_name && (
                        <Tooltip
                          title={log.template_name}
                          arrow
                          enterDelay={300}
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '1.25rem',
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              wordBreak: 'break-all',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                              fontWeight: 'medium',
                              display: 'block',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {log.template_name}
                          </Typography>
                        </Tooltip>
                      )}
                      {log.reference_metadata?.template_name && (
                        <Tooltip
                          title={log.reference_metadata.template_name}
                          arrow
                          enterDelay={300}
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '1.25rem',
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              wordBreak: 'break-all',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                              fontWeight: 'medium',
                              display: 'block',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {log.reference_metadata.template_name}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    {/* user_id field contains user email */}
                    <Tooltip
                      title={log.user_id || 'No user email'}
                      arrow
                      enterDelay={300}
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '1.25rem',
                          },
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.user_id}{' '}
                          {/* user_id field contains user email */}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Tooltip
                      title={log.original_input || 'No content'}
                      arrow
                      enterDelay={300}
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '1.25rem',
                          },
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {log.original_input?.substring(0, 100)}
                        {log.original_input?.length > 100 && '...'}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Badge
                        badgeContent={log.total_violations || 0}
                        color="error"
                      >
                        <SecurityIcon />
                      </Badge>
                      {log.was_anonymized && (
                        <Tooltip title="Content was anonymized">
                          <AnonymizeIcon color="warning" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <StandardizedChip
                      label={`${log.risk_score || 0}%`}
                      color={
                        log.risk_score > 70
                          ? 'error'
                          : log.risk_score > 30
                            ? 'warning'
                            : 'success'
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <StandardizedChip
                      label={log.was_anonymized ? 'Anonymized' : 'Clean'}
                      color={log.was_anonymized ? 'warning' : 'success'}
                    />
                  </TableCell>

                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => onViewDetails(log)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ mt: 3 }}>
            <PaginationBar
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount || 0}
              pageSize={pageSize}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              onPageClick={handlePageClick}
              onPageSizeChange={handlePageSizeChange}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default AuditTable;
